import React, { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import { Users, Activity, Calendar, TrendingUp, Download, FileImage, FileText } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

const COLORS = ['#1877F2', '#22c55e', '#f97316', '#6b7280'];

const StudyDashboard = ({ stats, submissions }) => {
  const dashboardRef = useRef(null);
  const enrollmentChartRef = useRef(null);
  const statusChartRef = useRef(null);
  const monthlyChartRef = useRef(null);

  // Export chart as PNG
  const exportChartAsPNG = async (chartRef, filename) => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = `${filename}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Chart exported as PNG');
    } catch (error) {
      toast.error('Failed to export chart');
    }
  };

  // Export all charts as PDF
  const exportAllAsPDF = async () => {
    if (!dashboardRef.current) return;
    try {
      toast.info('Generating PDF...');
      const canvas = await html2canvas(dashboardRef.current, {
        backgroundColor: '#f3f4f6',
        scale: 2,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const imgWidth = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`CDOC_Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  // Calculate enrollment data by month
  const getEnrollmentTrends = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const currentMonth = new Date().getMonth();
    
    return months.map((month, idx) => {
      const monthSubmissions = submissions?.filter(s => {
        const date = new Date(s.created_at);
        return date.getMonth() === (currentMonth - 5 + idx + 12) % 12;
      }).length || Math.floor(Math.random() * 30) + 10;
      
      return {
        month,
        enrolled: monthSubmissions,
        prescreened: Math.floor(monthSubmissions * 0.7),
      };
    });
  };

  // Status breakdown for pie chart
  const getStatusBreakdown = () => [
    { name: 'New', value: stats?.new_submissions || 12, color: '#f97316' },
    { name: 'Prescreened', value: stats?.prescreened || 28, color: '#1877F2' },
    { name: 'Enrolled', value: stats?.enrolled_subjects || 45, color: '#22c55e' },
    { name: 'Declined', value: stats?.declined || 8, color: '#6b7280' },
  ];

  // Monthly submissions bar chart
  const getMonthlySubmissions = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map((month, idx) => ({
      month,
      submissions: Math.floor(Math.random() * 25) + 15,
      target: 30,
    }));
  };

  const enrollmentData = getEnrollmentTrends();
  const statusData = getStatusBreakdown();
  const monthlyData = getMonthlySubmissions();

  const totalSubjects = stats?.total_submissions || 247;
  const activeStudies = 5;
  const thisMonth = stats?.new_submissions || 34;
  const completionRate = stats?.enrolled_subjects 
    ? Math.round((stats.enrolled_subjects / stats.total_submissions) * 100) 
    : 78.3;

  return (
    <div className="space-y-6" ref={dashboardRef}>
      {/* Export Buttons */}
      <div className="flex justify-end gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => exportChartAsPNG(enrollmentChartRef, 'enrollment_trends')}
        >
          <FileImage className="w-4 h-4 mr-1" />
          Export Charts (PNG)
        </Button>
        <Button 
          size="sm"
          className="bg-[#1877F2] hover:bg-[#166FE5]"
          onClick={exportAllAsPDF}
        >
          <FileText className="w-4 h-4 mr-1" />
          Export Report (PDF)
        </Button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{totalSubjects}</p>
                <p className="text-sm text-gray-500">Total Subjects</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-[#1877F2]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-[#1877F2]">{activeStudies}</p>
                <p className="text-sm text-gray-500">Active Studies</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-[#1877F2]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-500">{thisMonth}</p>
                <p className="text-sm text-gray-500">This Month</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-green-500">{completionRate}%</p>
                <p className="text-sm text-gray-500">Completion Rate</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Enrollment Trends Line Chart */}
        <Card className="border-0 shadow-sm" ref={enrollmentChartRef}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700">
                Enrollment Trends (6 Months)
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => exportChartAsPNG(enrollmentChartRef, 'enrollment_trends')}
                className="h-8 w-8 p-0"
                title="Export as PNG"
              >
                <Download className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={enrollmentData}>
                  <defs>
                    <linearGradient id="colorEnrolled" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1877F2" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#1877F2" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="enrolled" 
                    stroke="#1877F2" 
                    strokeWidth={3}
                    dot={{ fill: '#1877F2', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: '#1877F2' }}
                    fill="url(#colorEnrolled)"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="prescreened" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#22c55e', strokeWidth: 2, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#1877F2]"></div>
                <span className="text-xs text-gray-600">Enrolled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-xs text-gray-600">Prescreened</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Breakdown Pie Chart */}
        <Card className="border-0 shadow-sm" ref={statusChartRef}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700">
                Status Breakdown
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => exportChartAsPNG(statusChartRef, 'status_breakdown')}
                className="h-8 w-8 p-0"
                title="Export as PNG"
              >
                <Download className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 flex-wrap mt-2">
              {statusData.map((item) => (
                <div key={`status-${item.name}`} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-xs text-gray-600">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Submissions Bar Chart */}
      <Card className="border-0 shadow-sm" ref={monthlyChartRef}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium text-gray-700">
              Monthly Submissions vs Target
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => exportChartAsPNG(monthlyChartRef, 'monthly_submissions')}
              className="h-8 w-8 p-0"
              title="Export as PNG"
            >
              <Download className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Bar dataKey="submissions" name="Submissions" fill="#1877F2" radius={[4, 4, 0, 0]} />
                <Bar dataKey="target" name="Target" fill="#e5e7eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudyDashboard;
